import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { SEO } from '../components/SEO';
import api from '../lib/api';
import { CONTACT } from '../constants/testIds';

export default function ContactPage() {
  const { t } = useI18n();
  const fields = t('contact.fields');

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/contact', form);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Une erreur est survenue.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-background">
      <SEO
        title={t('contact.title')}
        description="Contactez Tahitian Move pour toute information sur la marque polynésienne fondée en 1977."
      />

      <section className="pt-40 pb-16 md:pt-48 md:pb-24 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="eyebrow">{t('contact.title')}</p>
          <h1 className="display text-6xl md:text-8xl lg:text-9xl mt-6 max-w-3xl">
            {t('contact.subtitle')}
          </h1>
          <p className="mt-8 text-ink-soft max-w-xl text-base md:text-lg leading-relaxed">
            {t('contact.intro')}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {success ? (
            <div
              data-testid={CONTACT.success}
              className="border border-lagoon/40 bg-lagoon/5 p-8 md:p-12 flex items-start gap-4"
            >
              <CheckCircle2 className="text-lagoon shrink-0 mt-1" />
              <div>
                <p className="font-serif text-2xl text-ink">{t('contact.success')}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label={fields.name} required>
                <input
                  data-testid={CONTACT.name}
                  required
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  className="tm-input"
                />
              </Field>
              <Field label={fields.email} required>
                <input
                  data-testid={CONTACT.email}
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  className="tm-input"
                />
              </Field>
              <Field label={fields.subject}>
                <input
                  data-testid={CONTACT.subject}
                  value={form.subject}
                  onChange={(e) => update('subject', e.target.value)}
                  className="tm-input"
                />
              </Field>
              <div className="hidden md:block" />
              <div className="md:col-span-2">
                <Field label={fields.message} required>
                  <textarea
                    data-testid={CONTACT.message}
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    className="tm-input resize-none"
                  />
                </Field>
              </div>
              {error && <p className="md:col-span-2 text-red-600 text-sm">{error}</p>}
              <div className="md:col-span-2 mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <p className="text-sm text-ink-muted">
                  {t('contact.proNotice')}{' '}
                  <Link to="/b2b" className="link-underline text-ink">{t('contact.proLink')}</Link>.
                </p>
                <button
                  data-testid={CONTACT.submit}
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-50"
                >
                  {submitting ? t('common.sending') : t('common.submit')}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="tm-label">{label}{required && <span className="text-lagoon"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
